// ATTENTION
// This whole component is creade with AI and not really code reviewed

import { Component, ElementRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { Store } from '@ngxs/store';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { PuzzleGameState } from '../../store/puzzle-game.state';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { IncMoveCounter, SetFinished, SetGameProgress } from '../../store/puzzle-game.actions';
import { Subscription } from 'rxjs';
import { Button } from '../../shared-module/buttons/button.def';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { HashiHelpComponent } from '../help/hashi-help.component';
import { HashiWinScreenComponent } from '../win-screen/hashi-win-screen.component';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';

interface HashiIsland {
  id: string;
  x: number;
  y: number;
  requiredBridges: number;
}

interface HashiBridgeKey {
  a: string;
  b: string;
}

@Component({
  selector: 'lib-app-hashi-board',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './hashi-board.component.html',
  styleUrls: ['./hashi-board.component.scss'],
})
export class HashiBoardComponent implements OnInit, OnDestroy {
  cellSize = 60;
  islandRadius = 18;
  bridgeStroke = 6;
  bridgeOffset = 7;
  hitStrokeWidth = 28;
  // Add internal padding so strokes and circles are not clipped at the SVG edges
  boardPadding = this.islandRadius + this.bridgeStroke + 4;

  islands: HashiIsland[] = [];
  islandById: Record<string, HashiIsland> = {};
  bridges: Record<string, number> = {};
  bridgeKeys: HashiBridgeKey[] = [];

  widthPX = 600;
  heightPX = 400;

  private draggingFrom: HashiIsland | null = null;
  private hoveredKeyS: string | null = null;
  private dragPreviewKeyS: string | null = null;
  private hoveredIslandId: string | null = null;

  private buttonSub: Subscription;
  private winDialogSub: Subscription;
  private helpDialogSub: Subscription;
  private settingsSub: Subscription;
  private puzzleID: string;

  showErrorsImmediately = false;

  constructor(
    public framework: PuzzleFrameworkService,
    private store: Store,
    private el: ElementRef,
    private winDialogService: PuzzleWinDialogService,
    private helpDialogService: PuzzleHelpDialogService,
    // Injecting settings modal service ensures its constructor runs and hooks SettingsMenu
    private settingsModalService: PuzzleSettingsModalService
  ) {
    this.winDialogSub = this.winDialogService.winDialogRegistered.subscribe(() => {
      this.winDialogService.injectComponent(HashiWinScreenComponent, {});
      this.winDialogService.showButtons([
        PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME,
        PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME,
      ]);
    });

    // Register help dialog contents for Hashi
    this.helpDialogSub = this.helpDialogService.helpDialogRegistered.subscribe(() => {
      this.helpDialogService.injectHelpComponent(HashiHelpComponent, { translationKey: 'HASHI' });
    });
  }

  ngOnInit(): void {
    const root = this.framework.puzzleConfig as any;

    // Support two formats:
    // 1) Old format: root.puzzle.game.islands
    // 2) New grid format: root.puzzle is number[][] and optional root.solution
    let nodes: HashiIsland[] = [];
    let puzzleID = '';

    if (root && root.puzzle && Array.isArray(root.puzzle) && Array.isArray(root.puzzle[0])) {
      const grid: number[][] = root.puzzle as number[][];
      for (let r = 0; r < grid.length; r++) {
        const row = grid[r];
        for (let c = 0; c < row.length; c++) {
          const val = row[c] as any;
          if (typeof val === 'number' && val > 0) {
            nodes.push({ id: `${r},${c}`, x: c, y: r, requiredBridges: val });
          }
        }
      }
      puzzleID = root.id || `hashi-${new Date().toISOString()}`;
    } else {
      const config = root.puzzle;
      const game = config.game;
      nodes = (game.nodes as HashiIsland[]).map((n) => ({ ...n }));
      puzzleID = config.id;
    }

    this.islands = nodes;
    this.islandById = {};
    this.islands.forEach((n) => (this.islandById[n.id] = n));

    // canvas size (include padding on all sides)
    this.widthPX = Math.max(...this.islands.map((n) => n.x), 0) * this.cellSize + this.cellSize + this.boardPadding * 2;
    this.heightPX =
      Math.max(...this.islands.map((n) => n.y), 0) * this.cellSize + this.cellSize + this.boardPadding * 2;

    // init bridges map for visible neighbor pairs
    const neighborPairs = this.computeNeighborPairs();
    neighborPairs.forEach((k) => {
      this.bridges[this.serializeKey(k)] = 0;
    });
    this.bridgeKeys = neighborPairs;

    this.puzzleID = puzzleID || `hashi-${new Date().toISOString()}`;
    this.store.dispatch(new SetPuzzleID({ puzzleID: this.puzzleID }));

    // init generic game state (reuse existing type)
    this.store.select(PuzzleGameState.getByID(this.puzzleID)).subscribe((puzzleGame) => {
      if (puzzleGame === null) {
        this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.HashiGame, 0, undefined, undefined);
        return;
      }
    });

    // handle button bar events (Check/Reset)
    this.buttonSub = this.framework.buttonPressed.subscribe((button) => {
      switch (button) {
        case Button.Check:
          this.onCheck();
          break;
        case Button.Reset:
          this.resetPuzzle();
          break;
      }
    });

    // subscribe to settings: showErrorsImmediately
    this.settingsSub = this.store
      .select((state) => state.puzzleGameState.showErrorsImmediately)
      .subscribe((enabled) => {
        console.log('HashiBoardComponent: showErrorsImmediately =', enabled);
        this.showErrorsImmediately = enabled;
      });
  }

  ngOnDestroy(): void {
    if (this.buttonSub) this.buttonSub.unsubscribe();
    if (this.winDialogSub) this.winDialogSub.unsubscribe();
    if (this.helpDialogSub) this.helpDialogSub.unsubscribe();
    if (this.settingsSub) this.settingsSub.unsubscribe();
  }

  // UI helpers
  islandCenter(n: HashiIsland) {
    return { x: this.boardPadding + n.x * this.cellSize, y: this.boardPadding + n.y * this.cellSize };
  }

  lineH(key: HashiBridgeKey) {
    const a = this.islandById[key.a];
    const b = this.islandById[key.b];
    const y = this.islandCenter(a).y;
    const x1 = Math.min(this.islandCenter(a).x, this.islandCenter(b).x);
    const x2 = Math.max(this.islandCenter(a).x, this.islandCenter(b).x);
    return { x1, y1: y, x2, y2: y };
  }
  lineV(key: HashiBridgeKey) {
    const a = this.islandById[key.a];
    const b = this.islandById[key.b];
    const x = this.islandCenter(a).x;
    const y1 = Math.min(this.islandCenter(a).y, this.islandCenter(b).y);
    const y2 = Math.max(this.islandCenter(a).y, this.islandCenter(b).y);
    return { x1: x, y1, x2: x, y2 };
  }

  getOrientation(key: HashiBridgeKey): 'h' | 'v' | null {
    const a = this.islandById[key.a];
    const b = this.islandById[key.b];
    if (!a || !b) return null;
    if (a.y === b.y) return 'h';
    if (a.x === b.x) return 'v';
    return null;
  }

  serializeKey(key: HashiBridgeKey) {
    return key.a < key.b ? `${key.a}|${key.b}` : `${key.b}|${key.a}`;
  }

  mouseDownIsland(n: HashiIsland) {
    this.draggingFrom = n;
  }

  @HostListener('document:mouseup', ['$event'])
  onDocMouseUp(ev: MouseEvent) {
    if (this.draggingFrom) {
      const svg: SVGSVGElement = this.el.nativeElement.querySelector('svg');
      if (svg) {
        const rect = svg.getBoundingClientRect();
        const x = (ev.clientX - rect.left) * (this.widthPX / rect.width);
        const y = (ev.clientY - rect.top) * (this.heightPX / rect.height);
        const from = this.draggingFrom;
        const c = this.islandCenter(from);
        const dx = x - c.x;
        const dy = y - c.y;
        let target: HashiIsland | null = null;
        if (Math.abs(dx) >= Math.abs(dy)) {
          const dir = dx >= 0 ? 1 : -1;
          target = this.raycast(from, dir, 0);
        } else {
          const dir = dy >= 0 ? 1 : -1;
          target = this.raycast(from, 0, dir);
        }
        if (target) {
          this.tryCycleBridge({ a: from.id, b: target.id });
        }
      }
    }
    this.draggingFrom = null;
    this.dragPreviewKeyS = null;
  }

  @HostListener('document:mousemove', ['$event'])
  onDocMouseMove(ev: MouseEvent) {
    if (!this.draggingFrom) {
      if (this.dragPreviewKeyS !== null) this.dragPreviewKeyS = null;
      return;
    }
    const svg: SVGSVGElement = this.el.nativeElement.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = (ev.clientX - rect.left) * (this.widthPX / rect.width);
    const y = (ev.clientY - rect.top) * (this.heightPX / rect.height);
    const from = this.draggingFrom;
    const c = this.islandCenter(from);
    const dx = x - c.x;
    const dy = y - c.y;
    let target: HashiIsland | null = null;
    if (Math.abs(dx) >= Math.abs(dy)) {
      const dir = dx >= 0 ? 1 : -1;
      target = this.raycast(from, dir, 0);
    } else {
      const dir = dy >= 0 ? 1 : -1;
      target = this.raycast(from, 0, dir);
    }
    if (!target) {
      this.dragPreviewKeyS = null;
      return;
    }
    const k = this.normalizeKey({ a: from.id, b: target.id });
    if (!k) {
      this.dragPreviewKeyS = null;
      return;
    }
    const current = this.bridges[this.serializeKey(k)] ?? 0;
    const next = (current + 1) % 3;
    if (next > current && this.createsCrossing(k, next)) {
      this.dragPreviewKeyS = null; // illegal increase -> no preview
      return;
    }
    this.dragPreviewKeyS = this.serializeKey(k);
  }

  mouseUpIsland(n: HashiIsland) {
    if (!this.draggingFrom) return;
    const from = this.draggingFrom;
    this.draggingFrom = null;
    if (from.id === n.id) return;
    this.tryCycleBridge({ a: from.id, b: n.id });
  }

  cycleBridge(key: HashiBridgeKey) {
    this.tryCycleBridge(key);
  }

  enterBridge(key: HashiBridgeKey) {
    this.hoveredKeyS = this.serializeKey(key);
  }

  leaveBridge() {
    this.hoveredKeyS = null;
  }

  enterIsland(n: HashiIsland) {
    this.hoveredIslandId = n.id;
  }

  leaveIsland() {
    this.hoveredIslandId = null;
  }

  getPreviewCountFor(key: HashiBridgeKey): number {
    const sk = this.serializeKey(key);
    // prioritize drag preview over hover of a single bridge segment
    const active = this.dragPreviewKeyS !== null ? this.dragPreviewKeyS : this.hoveredKeyS;
    if (active === sk) {
      const k = this.normalizeKey(key);
      if (!k) return 0;
      const current = this.bridges[sk] ?? 0;
      const next = (current + 1) % 3;
      if (next === 0) return 0;
      if (next > current && this.createsCrossing(k, next)) return 0;
      return next;
    }

    // If hovering an island, show previews for all neighbor bridges touching that island
    if (this.hoveredIslandId) {
      const [a, b] = sk.split('|');
      if (a === this.hoveredIslandId || b === this.hoveredIslandId) {
        const k = this.normalizeKey(key);
        if (!k) return 0;
        const current = this.bridges[sk] ?? 0;
        const next = (current + 1) % 3;
        if (next === 0) return 0;
        if (next > current && this.createsCrossing(k, next)) return 0;
        return next;
      }
    }

    return 0;
  }

  private tryCycleBridge(key: HashiBridgeKey) {
    const k = this.normalizeKey(key);
    if (!k) return;

    // validate visibility (no node between) and orientation
    if (!this.isVisiblePair(k)) return; // illegal -> blocked immediately

    // determine delta change
    const skey = this.serializeKey(k);
    const current = this.bridges[skey] ?? 0;
    const next = (current + 1) % 3; // 0->1->2->0

    // Allow increasing beyond node capacity; overpopulated islands will be flagged visually
    // (retain crossing checks and max 2 per pair via cycling 0→1→2→0)

    // check crossings on increase
    if (next > current) {
      if (this.createsCrossing(k, next)) return;
    }

    // apply change
    this.bridges[skey] = next;
    this.store.dispatch(new IncMoveCounter({ puzzleID: this.puzzleID }));

    // Recalculate progress fresh from all correct bridges
    // A bridge counts as correct only if both touching islands are valid (degree <= required)
    this.updateProgress();

    // Auto-finish when solved to show win screen
    if (this.isSolved()) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
    }
  }

  private normalizeKey(key: HashiBridgeKey): HashiBridgeKey | null {
    const a = this.islandById[key.a];
    const b = this.islandById[key.b];
    if (!a || !b) return null;
    if (!(a.x === b.x || a.y === b.y)) return null; // only orthogonal
    // ensure this pair is a neighbor pair
    const neighbors = this.computeNeighborPairsFor(a);
    const found = neighbors.find((p) => (p.a === a.id && p.b === b.id) || (p.a === b.id && p.b === a.id));
    return found ? (a.id < b.id ? { a: a.id, b: b.id } : { a: b.id, b: a.id }) : null;
  }

  private currentDegree(islandId: string): number {
    let sum = 0;
    for (const sk of Object.keys(this.bridges)) {
      const [sA, sB] = sk.split('|');
      if (sA === islandId || sB === islandId) {
        sum += this.bridges[sk];
      }
    }
    return sum;
  }

  isIslandSatisfied(n: HashiIsland): boolean {
    return this.currentDegree(n.id) >= n.requiredBridges;
  }

  isIslandOverpopulated(n: HashiIsland): boolean {
    return this.currentDegree(n.id) > n.requiredBridges;
  }

  private computeNeighborPairs(): HashiBridgeKey[] {
    const result: HashiBridgeKey[] = [];
    for (const n of this.islands) {
      const horiz = this.raycast(n, 1, 0);
      const vert = this.raycast(n, 0, 1);
      if (horiz) result.push(this.sortKey({ a: n.id, b: horiz.id }));
      if (vert) result.push(this.sortKey({ a: n.id, b: vert.id }));
    }
    // unique
    const seen = new Set<string>();
    const uniq: HashiBridgeKey[] = [];
    for (const k of result) {
      const s = this.serializeKey(k);
      if (!seen.has(s)) {
        seen.add(s);
        uniq.push(k);
      }
    }
    return uniq;
  }

  private computeNeighborPairsFor(n: HashiIsland): HashiBridgeKey[] {
    const out: HashiBridgeKey[] = [];
    const h = this.raycast(n, 1, 0);
    const h2 = this.raycast(n, -1, 0);
    const v = this.raycast(n, 0, 1);
    const v2 = this.raycast(n, 0, -1);
    if (h) out.push(this.sortKey({ a: n.id, b: h.id }));
    if (h2) out.push(this.sortKey({ a: n.id, b: h2.id }));
    if (v) out.push(this.sortKey({ a: n.id, b: v.id }));
    if (v2) out.push(this.sortKey({ a: n.id, b: v2.id }));
    // unique
    const seen = new Set<string>();
    const uniq: HashiBridgeKey[] = [];
    for (const k of out) {
      const s = this.serializeKey(k);
      if (!seen.has(s)) {
        seen.add(s);
        uniq.push(k);
      }
    }
    return uniq;
  }

  private sortKey(k: HashiBridgeKey): HashiBridgeKey {
    return k.a < k.b ? k : { a: k.b, b: k.a };
  }

  private raycast(n: HashiIsland, dx: number, dy: number): HashiIsland | null {
    // find the next node in the given direction without skipping through another node
    let x = n.x + dx;
    let y = n.y + dy;
    while (true) {
      const hit = this.islands.find((m) => m.x === x && m.y === y);
      if (hit) return hit;
      if (x < 0 || y < 0 || x > 1000 || y > 1000) return null;
      // continue stepping until we either hit another node or exceed reasonable bounds
      x += dx;
      y += dy;
    }
  }

  private isVisiblePair(k: HashiBridgeKey): boolean {
    // already ensured orthogonal; ensure no other node lies strictly between
    const a = this.islandById[k.a];
    const b = this.islandById[k.b];
    if (a.x === b.x) {
      const minY = Math.min(a.y, b.y);
      const maxY = Math.max(a.y, b.y);
      return !this.islands.some((n) => n.x === a.x && n.y > minY && n.y < maxY);
    } else if (a.y === b.y) {
      const minX = Math.min(a.x, b.x);
      const maxX = Math.max(a.x, b.x);
      return !this.islands.some((n) => n.y === a.y && n.x > minX && n.x < maxX);
    }
    return false;
  }

  private createsCrossing(k: HashiBridgeKey, nextCount: number): boolean {
    // If we increase to 1 or 2 on this segment, ensure the geometric segment will not intersect any existing perpendicular non-zero segment
    const seg = this.segmentOf(k);
    for (const otherKey of this.bridgeKeys) {
      const sk = this.serializeKey(otherKey);
      const cnt = this.bridges[sk] ?? 0;
      if (cnt === 0) continue;
      const oseg = this.segmentOf(otherKey);
      if (this.segmentsCross(seg, oseg)) return true;
    }
    return false;
  }

  private segmentOf(k: HashiBridgeKey) {
    const a = this.islandById[k.a];
    const b = this.islandById[k.b];
    const A = this.islandCenter(a);
    const B = this.islandCenter(b);
    if (A.x === B.x) {
      const x = A.x;
      const y1 = Math.min(A.y, B.y);
      const y2 = Math.max(A.y, B.y);
      return { type: 'v', x, y1, y2 } as any;
    } else {
      const y = A.y;
      const x1 = Math.min(A.x, B.x);
      const x2 = Math.max(A.x, B.x);
      return { type: 'h', y, x1, x2 } as any;
    }
  }

  private segmentsCross(a: any, b: any): boolean {
    // horizontal vs vertical only
    if (a.type === 'h' && b.type === 'v') {
      return b.x > a.x1 && b.x < a.x2 && a.y > b.y1 && a.y < b.y2;
    }
    if (a.type === 'v' && b.type === 'h') {
      return a.x > b.x1 && a.x < b.x2 && b.y > a.y1 && b.y < a.y2;
    }
    return false;
  }

  private computeProgress(): number {
    // Strict progress: only count bridges that connect two valid islands (degree <= required) for both endpoints.
    // Each such bridge contributes to both endpoints; per-node contributions are capped by requiredBridges.
    let totalReq = 0;
    const degree: Record<string, number> = {};
    const effective: Record<string, number> = {};

    for (const n of this.islands) {
      totalReq += n.requiredBridges;
      degree[n.id] = 0;
      effective[n.id] = 0;
    }

    // First pass: compute degrees from current bridges
    for (const sk of Object.keys(this.bridges)) {
      const cnt = this.bridges[sk] ?? 0;
      if (cnt <= 0) continue;
      const [a, b] = sk.split('|');
      if (degree[a] !== undefined) degree[a] += cnt;
      if (degree[b] !== undefined) degree[b] += cnt;
    }

    // Determine which islands are valid (not overpopulated)
    const valid: Record<string, boolean> = {};
    for (const n of this.islands) {
      valid[n.id] = degree[n.id] <= n.requiredBridges;
    }

    // Second pass: accumulate only bridges whose both endpoints are valid
    for (const sk of Object.keys(this.bridges)) {
      const cnt = this.bridges[sk] ?? 0;
      if (cnt <= 0) continue;
      const [a, b] = sk.split('|');
      if (valid[a] && valid[b]) {
        if (effective[a] !== undefined) effective[a] += cnt;
        if (effective[b] !== undefined) effective[b] += cnt;
      }
    }

    // Sum capped contributions per node
    let totalHave = 0;
    for (const n of this.islands) {
      totalHave += Math.min(effective[n.id], n.requiredBridges);
    }

    return totalReq > 0 ? totalHave / totalReq : 0;
  }

  private updateProgress(progress?: number) {
    const p = progress === undefined ? this.computeProgress() : progress;
    this.store.dispatch(new SetGameProgress({ puzzleID: this.puzzleID, progress: p }));
  }

  private resetPuzzle() {
    // reset all bridge counts to zero
    Object.keys(this.bridges).forEach((k) => (this.bridges[k] = 0));
    // re-init generic game state (progress, counters, finished flag)
    this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.HashiGame, 0);
    // update progress UI explicitly
    this.updateProgress(0);
  }

  private checkFlash = false;
  private checkErrorNodeIds: Set<string> = new Set<string>();
  private checkTimeout: any;

  private onCheck() {
    const ok = this.isSolved();
    if (ok) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
      return;
    }

    // Compute error islands for visual feedback
    const errors = new Set<string>();

    // Only circle islands whose degree does NOT equal the required number of bridges
    for (const n of this.islands) {
      if (this.currentDegree(n.id) !== n.requiredBridges) {
        errors.add(n.id);
      }
    }

    // Trigger a short highlight flash
    this.checkErrorNodeIds = errors;
    this.checkFlash = true;
    if (this.checkTimeout) {
      clearTimeout(this.checkTimeout);
      this.checkTimeout = undefined;
    }
    this.checkTimeout = window.setTimeout(() => {
      this.checkFlash = false;
    }, 1000);
  }

  private connectedComponent(): Set<string> {
    const adj: Record<string, string[]> = {};
    this.islands.forEach((n) => (adj[n.id] = []));
    for (const key of this.bridgeKeys) {
      const cnt = this.bridges[this.serializeKey(key)] ?? 0;
      if (cnt > 0) {
        adj[key.a].push(key.b);
        adj[key.b].push(key.a);
      }
    }
    const start = this.islands[0]?.id;
    const seen = new Set<string>();
    if (!start) return seen;
    const stack = [start];
    while (stack.length) {
      const cur = stack.pop()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      for (const nb of adj[cur]) if (!seen.has(nb)) stack.push(nb);
    }
    return seen;
  }

  hasCheckError(n: HashiIsland): boolean {
    return this.checkFlash && this.checkErrorNodeIds.has(n.id);
  }

  private isSolved(): boolean {
    // 1) degrees
    for (const n of this.islands) {
      if (this.currentDegree(n.id) !== n.requiredBridges) return false;
    }
    // 2) connectivity
    if (!this.isConnected()) return false;
    // 3) no crossings already enforced on creation, but assert
    for (const key of this.bridgeKeys) {
      const cnt = this.bridges[this.serializeKey(key)] ?? 0;
      if (cnt === 0) continue;
      if (this.createsCrossing(key, cnt)) return false;
    }
    return true;
  }

  private isConnected(): boolean {
    // build adjacency from existing non-zero bridges
    const adj: Record<string, string[]> = {};
    this.islands.forEach((n) => (adj[n.id] = []));
    for (const key of this.bridgeKeys) {
      const cnt = this.bridges[this.serializeKey(key)] ?? 0;
      if (cnt > 0) {
        adj[key.a].push(key.b);
        adj[key.b].push(key.a);
      }
    }
    // dfs
    const start = this.islands[0]?.id;
    if (!start) return false;
    const seen = new Set<string>();
    const stack = [start];
    while (stack.length) {
      const cur = stack.pop()!;
      if (seen.has(cur)) continue;
      seen.add(cur);
      for (const nb of adj[cur]) if (!seen.has(nb)) stack.push(nb);
    }
    return seen.size === this.islands.length;
  }
}
